import { Injectable } from '@angular/core';
import moment, { Moment } from 'moment';
import { LuceneExpression, term } from 'lucene';

import { ClaimStatus, PENDING_STATUS_LIST, CLOSED_STATUS_LIST } from '../models/quality-issue';
import { LuceneService } from '../../shared/services/lucene.service';

@Injectable({ providedIn: 'root' })
export class QualityIssueFilterService {
	constructor(
		private readonly luceneService: LuceneService
	) {}

	createFiltersFromClaimStatusList(selectedStatus: ClaimStatus[], searchText?: string) {
		return this.appendSearchText(this.luceneService.concatAll('status', selectedStatus), searchText);
	}

	createDefaultFilters(selectedStatus?: Set<string>, searchText?: string) {
		return !selectedStatus || selectedStatus.size === 0
			? this.createDefaultFilterSet(searchText)
			: this.appendSearchText(
				this.generateFiltersFromSelectedStatus(selectedStatus),
				searchText
			);
	}

	createFilterClosedDate(from: Moment | '*' = moment().subtract(1, 'month'), to: Moment | '*' = '*') {
		return this.luceneService.createFromDate('closedDate', from, to);
	}

	generateExpresionForSearch(searchText: string) {
		return this.luceneService.concat(
			this.luceneService.create('customerReference', `*${ term.escape(searchText) }*`),
			this.luceneService.create('supplierIssueNumber', `*${ term.escape(searchText) }*`),
			'OR'
		);
	}

	concatFilters(exp1: LuceneExpression, exp2: LuceneExpression, opr: 'AND' | 'OR' = 'AND', parentheised = true) {
		return this.luceneService.concat(exp1, exp2, opr, parentheised);
	}

	createPaidFilter() {
		return this.createFiltersByClosedClaimStatus(ClaimStatus.PAID);
	}

	createRefusedFilter() {
		return this.createFiltersByClosedClaimStatus(ClaimStatus.REFUSED);
	}

	buildClaimTypeFilter(claimType: string) {
		return this.luceneService.create('claimType', claimType);
	}

	private createFiltersByClosedClaimStatus(claimStatus = ClaimStatus.PAID) {
		return this.luceneService.concatAll(
			'status',
			[claimStatus === ClaimStatus.PAID ? ClaimStatus.PAID : ClaimStatus.REFUSED]
		);
	}

	private createFiltersByClaimStatus(claimStatus = 'pending') {
		return claimStatus === 'pending'
			? this.luceneService.concatAll('status', Array.from(PENDING_STATUS_LIST))
			: this.luceneService.concatAll('status', Array.from(CLOSED_STATUS_LIST));
	}

	private appendSearchText(filter: LuceneExpression, searchText?: string) {
		return searchText ? this.luceneService.concat(
			filter,
			this.generateExpresionForSearch(searchText),
			'AND'
		)
			: filter;
	}

	private generateFiltersFromSelectedStatus(selectedStatus: Set<string>) {
		const selectedStatusArray = Array.from(selectedStatus);
		let generatedFilter = this.createFiltersByClaimStatus(selectedStatusArray[0]);
		for (let i = 1; i < selectedStatusArray.length; i++) {
			generatedFilter = this.luceneService.concat(
				generatedFilter,
				this.createFiltersByClaimStatus(selectedStatusArray[i]),
				'OR'
			);
		}
		return generatedFilter;
	}

	private createDefaultFilterSet(searchText?: string) {
		return this.appendSearchText(
			this.luceneService.concat(
				this.createFilterClosedDate(),
				this.luceneService.concatAll('status', [...PENDING_STATUS_LIST, ClaimStatus.DRAFT]),
				'OR',
				true
			),
			searchText
		);
	}
}
